import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

export interface FactoryItem {
  name: string;
  region: string;
  address?: string;
  contactPerson?: string;
  designation?: string;
  phoneNumber?: string;
  inspections: number;
  usersCount: number;
  storageGB: number;
  appVersion: string;
  outdatedDevices: number;
  status: 'Optimal' | 'Warning' | 'Critical';
  uptime: string;
  enrolledDate: string;
  allowedUsers?: number;
  subscriptionStatus?: 'Trial' | 'Active' | 'Grace' | 'Expired';
  subscriptionStart?: string;
  subscriptionEnd?: string;
  isDisabled?: boolean;
}

export interface UserItem {
  id: string;
  name: string;
  email: string;
  password?: string;
  employeeId: string;
  lastLoggedIn: string;
  inspectionsCount: number;
  appVersion: string;
  status: 'Active' | 'Disabled';
  factoryName: string;
  role?: 'Admin' | 'Staff';
  modules?: string[];
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  if (dateStr.includes(' ') && dateStr.length === 11 && !dateStr.includes('-')) {
    return dateStr;
  }
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const year = parts[0];
  const monthNum = parseInt(parts[1], 10);
  const day = parts[2];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = months[monthNum - 1] || 'Jan';
  const formattedDay = day.padStart(2, '0');
  return `${formattedDay} ${monthName} ${year}`;
};

const mapMonthToNum = (monthName: string): string => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const idx = months.indexOf(monthName);
  if (idx === -1) return '01';
  return (idx + 1).toString().padStart(2, '0');
};

const dateToYMD = (dateStr: string): string => {
  if (!dateStr) return '';
  if (dateStr.includes('-') && dateStr.split('-').length === 3) {
    return dateStr;
  }
  const parts = dateStr.trim().split(/\s+/);
  if (parts.length === 3) {
    const day = parts[0].padStart(2, '0');
    const month = mapMonthToNum(parts[1]);
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {}
  return '';
};

const ymdToReadable = (ymdStr: string): string => {
  if (!ymdStr) return '';
  const parts = ymdStr.split('-');
  if (parts.length !== 3) return ymdStr;
  const year = parts[0];
  const monthNum = parseInt(parts[1], 10) || 1;
  const day = parts[2];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = months[monthNum - 1] || 'Jan';
  const formattedDay = parseInt(day, 10).toString().padStart(2, '0');
  return `${formattedDay} ${monthName} ${year}`;
};

const formatCurrentDateTime = () => {
  const date = new Date();
  const day = String(date.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const formattedHours = String(hours).padStart(2, '0');
  return `${day} ${month} ${year} ${formattedHours}:${minutes} ${ampm}`;
};

const getDeviceDetails = (user: UserItem) => {
  const hash = user.employeeId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  const devices = [
    { name: "Samsung Galaxy Tab Active4 Pro Enterprise", os: "Android 13 (OS Build TE.26)", storage: "128 GB Enterprise Storage (42 GB Free)", ram: "6 GB LPDDR5", screen: "10.1 INCH IPS TFT Touchscreen (Glove Enabled)", cellular: "5G Enterprise (Sub-6) + LTE / Wi-Fi 6" },
    { name: "Apple iPad Air (5th Gen) Rugged Enterprise", os: "iPadOS 16.5 Premium Secure", storage: "256 GB NVMe SSD (118 GB Free)", ram: "8 GB Unified Memory", screen: "10.9 INCH Liquid Retina Multi-Touch", cellular: "5G LTE Core / Gigabit Wi-Fi 6E" },
    { name: "Lenovo Tab K10 Pro SECURE Node", os: "Android 12 Secure Core v3", storage: "64 GB eMMC (18 GB Free)", ram: "4 GB LPDDR4x", screen: "10.3 INCH Full HD+ Low Blue Light", cellular: "4G LTE Advanced Cat 12 / Wi-Fi 5" },
    { name: "Panasonic Toughbook FZ-G2 Tactical Tablet", os: "Windows 11 IoT Enterprise LTSC", storage: "512 GB Rugged NVMe SSD (340 GB Free)", ram: "16 GB Single Channel DDR4", screen: "10.1 INCH WUXGA Outdoor Readable (800 nits)", cellular: "4G LTE FirstNet Ready / Dedicated GPS" }
  ];
  
  const device = devices[hash % devices.length];
  const usageHours = Math.round(user.inspectionsCount * 2.3 + (hash % 120) + 45);
  
  return {
    deviceName: device.name,
    osVersion: device.os,
    storage: device.storage,
    ram: device.ram,
    screenSize: device.screen,
    cellularType: device.cellular,
    usageTime: `${usageHours} active operating hours`,
  };
};

const getUserAvatarUrl = (name: string) => {
  const avatars: Record<string, string> = {
    "Prabhakaran": "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=120&h=120",
    "Britto": "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=120&h=120",
    "Karthik": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=120&h=120",
    "Diya Krishna": "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=120&h=120",
    "Santhosh": "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=120&h=120",
    "Arun Kumar": "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=120&h=120",
    "Vijay": "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=120&h=120",
    "Sandhya": "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=120&h=120",
    "Balaji": "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=120&h=120",
    "Deepak": "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&q=80&w=120&h=120",
    "Abhishek": "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=120&h=120",
    "Divya": "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&q=80&w=120&h=120"
  };
  return avatars[name] || `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=120&h=120`;
};

const INITIAL_FACTORIES: FactoryItem[] = [
  {
    name: "Palm Fiber",
    region: "Karur",
    address: "12/4, Factory Bypass Road, Karur, Tamil Nadu - 639002",
    contactPerson: "Shanmugam",
    designation: "General Manager",
    phoneNumber: "+91 94432 10123",
    inspections: 284,
    usersCount: 3,
    storageGB: 92.4,
    appVersion: "v1.4.2",
    outdatedDevices: 0,
    status: "Optimal",
    uptime: "99.8%",
    enrolledDate: "15 Jan 2023",
    allowedUsers: 50,
    subscriptionStatus: "Active",
    subscriptionStart: "15 Jan 2025",
    subscriptionEnd: "15 Jan 2027",
    isDisabled: false
  },
  {
    name: "KKP Textile",
    region: "Coimbatore",
    address: "45-A, Avinashi Road, Peelamedu, Coimbatore, Tamil Nadu - 641004",
    contactPerson: "Kannan Palanisamy",
    designation: "Operations Director",
    phoneNumber: "+91 98940 55432",
    inspections: 310,
    usersCount: 3,
    storageGB: 110.1,
    appVersion: "v1.4.2",
    outdatedDevices: 1,
    status: "Optimal",
    uptime: "99.5%",
    enrolledDate: "20 Nov 2022",
    allowedUsers: 80,
    subscriptionStatus: "Active",
    subscriptionStart: "20 Nov 2025",
    subscriptionEnd: "20 Nov 2026",
    isDisabled: false
  },
  {
    name: "Jayvel",
    region: "Chennai",
    address: "B-12, SIPCOT Industrial Park, Irrungattukottai, Chennai, Tamil Nadu - 602105",
    contactPerson: "Vijay Raghavan",
    designation: "Plant Head",
    phoneNumber: "+91 97890 12345",
    inspections: 320,
    usersCount: 2,
    storageGB: 84.6,
    appVersion: "v1.4.2",
    outdatedDevices: 0,
    status: "Optimal",
    uptime: "99.9%",
    enrolledDate: "18 May 2021",
    allowedUsers: 45,
    subscriptionStatus: "Active",
    subscriptionStart: "18 May 2025",
    subscriptionEnd: "18 May 2027",
    isDisabled: false
  },
  {
    name: "Mats & More",
    region: "Bangalore",
    address: "Plot No. 89, Peenya Industrial Area 2nd Stage, Bangalore, Karnataka - 560058",
    contactPerson: "Balaji Rao",
    designation: "Superintendent",
    phoneNumber: "+91 80234 56789",
    inspections: 195,
    usersCount: 2,
    storageGB: 124.5,
    appVersion: "v1.4.1",
    outdatedDevices: 2,
    status: "Warning",
    uptime: "98.2%",
    enrolledDate: "04 Aug 2023",
    allowedUsers: 30,
    subscriptionStatus: "Grace",
    subscriptionStart: "04 Aug 2025",
    subscriptionEnd: "04 Sep 2026",
    isDisabled: false
  },
  {
    name: "Synergy",
    region: "Karur",
    address: "78, Sengunthapuram Main Road, Karur, Tamil Nadu - 639001",
    contactPerson: "Prabhakaran",
    designation: "Managing Partner",
    phoneNumber: "+91 94440 98765",
    inspections: 142,
    usersCount: 2,
    storageGB: 45.2,
    appVersion: "v1.3.8",
    outdatedDevices: 4,
    status: "Critical",
    uptime: "96.4%",
    enrolledDate: "30 Mar 2022",
    allowedUsers: 25,
    subscriptionStatus: "Expired",
    subscriptionStart: "30 Mar 2024",
    subscriptionEnd: "30 Mar 2025",
    isDisabled: true
  }
];

const INITIAL_USERS: UserItem[] = [
  // Palm Fiber
  {
    id: "u1",
    name: "Prabhakaran",
    email: "prabhakaran@qamate.com",
    password: "Prabhakaran@PalmFiber12",
    employeeId: "EMP-2023-001",
    lastLoggedIn: "17 Jun 2026 02:32 PM",
    inspectionsCount: 142,
    appVersion: "v1.4.2",
    status: "Active",
    factoryName: "Palm Fiber",
    role: "Admin"
  },
  {
    id: "u2",
    name: "Britto",
    email: "britto@qamate.com",
    employeeId: "EMP-2023-156",
    lastLoggedIn: "16 Jun 2026 09:12 AM",
    inspectionsCount: 98,
    appVersion: "v1.4.2",
    status: "Active",
    factoryName: "Palm Fiber",
    role: "Staff"
  },
  {
    id: "u3",
    name: "Karthik",
    email: "karthik@qamate.com",
    employeeId: "EMP-2024-002",
    lastLoggedIn: "17 Jun 2026 11:05 AM",
    inspectionsCount: 44,
    appVersion: "v1.4.2",
    status: "Active",
    factoryName: "Palm Fiber",
    role: "Staff"
  },

  // KKP Textile
  {
    id: "u4",
    name: "Diya Krishna",
    email: "diya.krishna@qamate.com",
    password: "Diya@KKPTextile98",
    employeeId: "EMP-2023-045",
    lastLoggedIn: "17 Jun 2026 03:45 PM",
    inspectionsCount: 180,
    appVersion: "v1.4.2",
    status: "Active",
    factoryName: "KKP Textile",
    role: "Admin"
  },
  {
    id: "u5",
    name: "Santhosh",
    email: "santhosh@qamate.com",
    employeeId: "EMP-2023-102",
    lastLoggedIn: "10 Jun 2026 08:23 AM",
    inspectionsCount: 45,
    appVersion: "v1.4.1",
    status: "Disabled",
    factoryName: "KKP Textile",
    role: "Staff"
  },
  {
    id: "u6",
    name: "Arun Kumar",
    email: "arun.kumar@qamate.com",
    employeeId: "EMP-2022-049",
    lastLoggedIn: "17 Jun 2026 10:15 AM",
    inspectionsCount: 85,
    appVersion: "v1.4.2",
    status: "Active",
    factoryName: "KKP Textile",
    role: "Staff"
  },

  // Jayvel
  {
    id: "u7",
    name: "Vijay",
    email: "vijay@qamate.com",
    password: "Vijay@Jayvel2026",
    employeeId: "EMP-2022-089",
    lastLoggedIn: "17 Jun 2026 04:10 PM",
    inspectionsCount: 220,
    appVersion: "v1.4.2",
    status: "Active",
    factoryName: "Jayvel",
    role: "Admin"
  },
  {
    id: "u8",
    name: "Sandhya",
    email: "sandhya@qamate.com",
    employeeId: "EMP-2023-189",
    lastLoggedIn: "17 Jun 2026 05:35 PM",
    inspectionsCount: 100,
    appVersion: "v1.4.2",
    status: "Active",
    factoryName: "Jayvel",
    role: "Staff"
  },

  // Mats & More
  {
    id: "u9",
    name: "Balaji",
    email: "balaji@qamate.com",
    password: "Balaji@MatsMore20",
    employeeId: "EMP-2022-012",
    lastLoggedIn: "15 Jun 2026 02:20 PM",
    inspectionsCount: 110,
    appVersion: "v1.4.1",
    status: "Active",
    factoryName: "Mats & More",
    role: "Admin"
  },
  {
    id: "u10",
    name: "Deepak",
    email: "deepak@qamate.com",
    employeeId: "EMP-2023-777",
    lastLoggedIn: "16 Jun 2026 11:40 AM",
    inspectionsCount: 85,
    appVersion: "v1.4.1",
    status: "Active",
    factoryName: "Mats & More",
    role: "Staff"
  },

  // Synergy
  {
    id: "u11",
    name: "Abhishek",
    email: "abhishek@qamate.com",
    employeeId: "EMP-2021-001",
    lastLoggedIn: "14 Jun 2026 09:00 AM",
    inspectionsCount: 72,
    appVersion: "v1.3.8",
    status: "Active",
    factoryName: "Synergy",
    role: "Admin"
  },
  {
    id: "u12",
    name: "Divya",
    email: "divya@qamate.com",
    employeeId: "EMP-2021-002",
    lastLoggedIn: "13 Jun 2026 08:30 AM",
    inspectionsCount: 70,
    appVersion: "v1.3.8",
    status: "Active",
    factoryName: "Synergy",
    role: "Staff"
  }
];

export const FactoryManagementPanel: React.FC = () => {
  // State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [factories, setFactories] = useState<FactoryItem[]>(INITIAL_FACTORIES);
  const [users, setUsers] = useState<UserItem[]>(INITIAL_USERS);
  const [selectedFactoryName, setSelectedFactoryName] = useState<string>(INITIAL_FACTORIES[0].name);

  // Searches & Filters (Factory)
  const [factorySearch, setFactorySearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Trial' | 'Active' | 'Grace' | 'Expired'>('All');

  // Searches & Filters (User)
  const [userSearch, setUserSearch] = useState('');
  const [userVersionFilter, setUserVersionFilter] = useState('All');

  // Add modals state
  const [showAddFactoryModal, setShowAddFactoryModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUserForDetail, setSelectedUserForDetail] = useState<UserItem | null>(null);
  const [copiedFactoryName, setCopiedFactoryName] = useState<string | null>(null);
  const [confirmFactoryState, setConfirmFactoryState] = useState<string | null>(null);

  // New Factory Form State
  const [newFactory, setNewFactory] = useState({
    name: '',
    region: '',
    address: '',
    contactPerson: '',
    designation: '',
    phoneNumber: '',
    subscriptionStatus: 'Trial' as 'Trial' | 'Active' | 'Grace' | 'Expired',
    subscriptionStart: '',
    subscriptionEnd: '',
    allowedUsers: 20,
  });

  // Edit Factory State
  const [showEditFactoryModal, setShowEditFactoryModal] = useState(false);
  const [editFactoryForm, setEditFactoryForm] = useState({
    name: '',
    region: '',
    address: '',
    contactPerson: '',
    designation: '',
    phoneNumber: '',
  });

  // New User Form State
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    employeeId: '',
    status: 'Active' as const,
    inspectionsCount: 0,
    appVersion: '',
    role: 'Staff' as 'Admin' | 'Staff',
    modules: ['Pilot', 'Fabric', 'Line', 'Final', 'Container'] as string[]
  });

  useEffect(() => {
    if (showAddFactoryModal && newFactory.subscriptionStatus === 'Trial') {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + 15);
      const expiryStr = `${expiry.getFullYear()}-${String(expiry.getMonth() + 1).padStart(2, '0')}-${String(expiry.getDate()).padStart(2, '0')}`;

      setNewFactory(prev => ({
        ...prev,
        allowedUsers: 20,
        subscriptionStart: todayStr,
        subscriptionEnd: expiryStr
      }));
    }
  }, [showAddFactoryModal, newFactory.subscriptionStatus]);

  const [roleTab, setRoleTab] = useState<'Admin' | 'Staff'>('Staff');

  // Derived current factory
  const selectedFactory = factories.find(f => f.name === selectedFactoryName) || factories[0];

  // Filter factories
  const filteredFactories = factories.filter(factory => {
    const matchesSearch = factory.name.toLowerCase().includes(factorySearch.toLowerCase()) ||
                          factory.region.toLowerCase().includes(factorySearch.toLowerCase());
    const subStatus = factory.subscriptionStatus ?? 'Active';
    const matchesStatus = statusFilter === 'All' || subStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Filter users for selected factory
  const selectedFactoryUsers = users.filter(user => user.factoryName === selectedFactoryName);
  const filteredUsers = selectedFactoryUsers.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
                          user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
                          user.employeeId.toLowerCase().includes(userSearch.toLowerCase());
    const matchesVersion = userVersionFilter === 'All' || user.appVersion === userVersionFilter;
    const userRole = user.role || 'Staff';
    const matchesRole = userRole === roleTab;
    return matchesSearch && matchesVersion && matchesRole;
  });

  // Unique app versions for selection
  const userVersions = Array.from(new Set(selectedFactoryUsers.map(u => u.appVersion)));

  const handleCreateFactory = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFactory.name || !newFactory.region || !newFactory.address) return;

    const now = new Date();
    const enrolledDateStr = now.toISOString().split('T')[0];
    const enrolledDateReadable = formatDate(enrolledDateStr);

    let finalStart = enrolledDateReadable;
    if (newFactory.subscriptionStart) {
      finalStart = ymdToReadable(newFactory.subscriptionStart);
    }

    let finalEnd = '';
    if (newFactory.subscriptionEnd) {
      finalEnd = ymdToReadable(newFactory.subscriptionEnd);
    } else {
      const trialDate = new Date();
      trialDate.setDate(trialDate.getDate() + 15);
      const trialDateStr = trialDate.toISOString().split('T')[0];
      finalEnd = formatDate(trialDateStr);
    }

    const added: FactoryItem = {
      name: newFactory.name,
      region: newFactory.region,
      address: newFactory.address,
      contactPerson: newFactory.contactPerson || undefined,
      designation: newFactory.designation || undefined,
      phoneNumber: newFactory.phoneNumber || undefined,
      inspections: 0,
      usersCount: 0,
      storageGB: 0,
      appVersion: "v1.4.2",
      outdatedDevices: 0,
      status: "Optimal",
      uptime: "100.0%",
      enrolledDate: enrolledDateReadable,
      allowedUsers: newFactory.allowedUsers,
      subscriptionStatus: newFactory.subscriptionStatus,
      subscriptionStart: finalStart,
      subscriptionEnd: finalEnd,
      isDisabled: false
    };

    setFactories([...factories, added]);
    setSelectedFactoryName(added.name);
    setShowAddFactoryModal(false);

    // Reset Form
    setNewFactory({
      name: '',
      region: '',
      address: '',
      contactPerson: '',
      designation: '',
      phoneNumber: '',
      subscriptionStatus: 'Trial',
      subscriptionStart: '',
      subscriptionEnd: '',
      allowedUsers: 20,
    });
  };

  const handleEditFactory = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFactoryForm.name || !editFactoryForm.region || !editFactoryForm.address) return;

    const oldName = selectedFactory.name;
    const newName = editFactoryForm.name;

    // Update factories array
    const updatedFactories = factories.map(f => {
      if (f.name === oldName) {
        return {
          ...f,
          name: newName,
          region: editFactoryForm.region,
          address: editFactoryForm.address,
          contactPerson: editFactoryForm.contactPerson || undefined,
          designation: editFactoryForm.designation || undefined,
          phoneNumber: editFactoryForm.phoneNumber || undefined,
        };
      }
      return f;
    });

    // Update users array if factory name changed
    if (oldName !== newName) {
      const updatedUsers = users.map(u => {
        if (u.factoryName === oldName) {
          return { ...u, factoryName: newName };
        }
        return u;
      });
      setUsers(updatedUsers);
    }

    setFactories(updatedFactories);
    setSelectedFactoryName(newName);
    setShowEditFactoryModal(false);
  };

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.employeeId) return;

    const added: UserItem = {
      id: 'u_' + Date.now(),
      name: newUser.name,
      email: newUser.email,
      password: newUser.role === 'Admin' ? `Admin@${newUser.name.replace(/[^a-zA-Z0-9]/g, '')}2026` : undefined,
      employeeId: newUser.employeeId,
      lastLoggedIn: formatCurrentDateTime(),
      inspectionsCount: Number(newUser.inspectionsCount),
      appVersion: newUser.appVersion || selectedFactory?.appVersion || 'v1.4.2',
      status: newUser.status,
      factoryName: selectedFactoryName,
      role: newUser.role,
      modules: newUser.modules
    };

    const updatedUsers = [...users, added];
    setUsers(updatedUsers);

    // Also update current factory's users count
    setFactories(factories.map(f => {
      if (f.name === selectedFactoryName) {
         return { ...f, usersCount: f.usersCount + 1 };
      }
      return f;
    }));

    setShowAddUserModal(false);

    // Reset Form
    setNewUser({
      name: '',
      email: '',
      employeeId: '',
      status: 'Active',
      inspectionsCount: 0,
      appVersion: '',
      role: 'Staff',
      modules: ['Pilot', 'Fabric', 'Line', 'Final', 'Container']
    });
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
        {/* LEFT COLUMN: Factories Directory (4 cols when open, 1 col when collapsed) */}
        <div className={`transition-all duration-300 ${
          isSidebarOpen 
            ? 'xl:col-span-4 w-full flex flex-col gap-6 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm' 
            : 'xl:col-span-1 w-full hidden xl:flex flex-col items-center gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm'
        }`}>
          {isSidebarOpen ? (
            <>
              {/* Expanded Sidebar Header */}
              <div className="flex items-center justify-between gap-4 pb-1">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Factory Directory</h2>
                  <p className="text-xs text-slate-500">Search and manage factories</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setShowAddFactoryModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-[#136bce] hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all"
                  >
                    <span className="material-symbols-outlined text-[16px]">add</span>
                    Add New
                  </button>
                  <button
                    onClick={() => setIsSidebarOpen(false)}
                    title="Collapse Directory"
                    className="w-8 h-8 rounded-xl text-slate-500 hover:text-[#136bce] hover:bg-blue-50/50 border border-slate-200 hover:border-blue-200 transition-all flex items-center justify-center bg-white shadow-xs active:scale-95"
                  >
                    <span className="material-symbols-outlined text-[18px]">keyboard_double_arrow_left</span>
                  </button>
                </div>
              </div>

              {/* Searching & Filter panel */}
              <div className="flex flex-col gap-3">
                <div className="relative">
                  <input
                    className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-4 text-xs placeholder-slate-400 focus:border-[#136bce] focus:outline-none focus:ring-1 focus:ring-[#136bce]"
                    placeholder="Search by factory name or region..."
                    type="text"
                    value={factorySearch}
                    onChange={(e) => setFactorySearch(e.target.value)}
                  />
                  <span className="material-symbols-outlined absolute left-3 top-2.5 text-[16px] text-slate-400">search</span>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">Filter status</span>
                  <div className="flex gap-1 overflow-x-auto py-1">
                    {(['All', 'Trial', 'Active', 'Grace', 'Expired'] as const).map((st) => (
                      <button
                        key={st}
                        onClick={() => setStatusFilter(st)}
                        className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg transition-all border whitespace-nowrap ${
                          statusFilter === st
                            ? 'bg-blue-50 text-[#136bce] border-blue-200'
                            : 'bg-white hover:bg-slate-50 text-slate-600 border-slate-200'
                        }`}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Factory Cards List */}
              <div className="space-y-3 max-h-[580px] overflow-y-auto pr-1">
                {filteredFactories.length === 0 ? (
                  <div className="py-8 text-center border border-dashed border-slate-200 rounded-xl">
                    <span className="material-symbols-outlined text-slate-300 text-[36px] mb-2">domain_disabled</span>
                    <p className="text-xs font-semibold text-slate-400">No matching factories found</p>
                  </div>
                ) : (
                  filteredFactories.map((factory) => {
                    const isSelected = factory.name === selectedFactoryName;
                    return (
                      <motion.div
                        key={factory.name}
                        onClick={() => setSelectedFactoryName(factory.name)}
                        className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer flex flex-col gap-3 relative overflow-hidden group ${
                          isSelected
                            ? 'bg-blue-50/60 border-[#136bce] ring-1 ring-[#136bce]/20 shadow-md translate-x-1'
                            : 'bg-white hover:bg-slate-50/80 border-slate-150 hover:border-slate-350 hover:shadow-sm'
                        }`}
                        whileTap={{ scale: 0.98 }}
                      >
                        {/* Left accent bar for highlighted item */}
                        {isSelected && (
                          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#136bce] rounded-r-md" />
                        )}

                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h4 className={`font-bold transition-colors text-sm leading-snug ${
                                isSelected ? 'text-[#136bce]' : 'text-slate-900 group-hover:text-[#136bce]'
                              }`}>{factory.name}</h4>
                              {factory.isDisabled && (
                                <span className="bg-rose-100 text-rose-850 text-[8px] font-black px-1.5 py-0.2 rounded-full border border-rose-200 uppercase tracking-wider">
                                  Disabled
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <span className="material-symbols-outlined text-[13px] text-slate-400">location_on</span>
                              {factory.region}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {/* Subscription Status Badge */}
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold border ${
                              (factory.subscriptionStatus ?? 'Active') === 'Trial' ? 'bg-blue-50 text-blue-700 border-blue-200/55' :
                              (factory.subscriptionStatus ?? 'Active') === 'Active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200/55' :
                              (factory.subscriptionStatus ?? 'Active') === 'Grace' ? 'bg-amber-50 text-amber-700 border-amber-200/55' :
                              'bg-rose-50 text-rose-700 border-rose-200/55'
                            }`}>
                              {(factory.subscriptionStatus ?? 'Active')}
                            </span>
                            
                            {/* Chevron / select indicator */}
                            <span className={`material-symbols-outlined text-[18px] transition-all duration-200 ${
                              isSelected 
                                ? 'text-[#136bce] scale-110' 
                                : 'text-slate-300 opacity-40 group-hover:opacity-100 group-hover:text-[#136bce] group-hover:translate-x-0.5'
                            }`}>
                              {isSelected ? 'arrow_circle_right' : 'chevron_right'}
                            </span>
                          </div>
                        </div>
   
                        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100/80 text-[11px] text-slate-500">
                          <div>
                            <span className="block text-[9px] text-slate-400 uppercase font-semibold">Active Users</span>
                            <span className="font-bold text-slate-800">
                              {users.filter(u => u.factoryName === factory.name && u.status === 'Active').length}
                            </span>
                          </div>
                          <div>
                            <span className="block text-[9px] text-slate-400 uppercase font-semibold">No of Insp</span>
                            <span className="font-bold text-[#136bce]">{factory.inspections}</span>
                          </div>
                          <div>
                            <span className="block text-[9px] text-slate-400 uppercase font-semibold">Enrolled</span>
                            <span className="font-bold text-slate-700">{factory.enrolledDate}</span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              {/* Collapsed Sidebar View (Desktop only rail) */}
              <button
                onClick={() => setIsSidebarOpen(true)}
                title="Expand Directory"
                className="w-10 h-10 rounded-xl text-slate-500 hover:text-[#136bce] hover:bg-blue-50 hover:border-blue-200 bg-slate-50 border border-slate-200/80 transition-all flex items-center justify-center shadow-sm active:scale-95"
              >
                <span className="material-symbols-outlined text-[20px] font-bold">keyboard_double_arrow_right</span>
              </button>

              <div className="w-full h-[1px] bg-slate-100" />

              {/* Total Qty Indicator */}
              <div className="flex flex-col items-center justify-center text-center">
                <span className="text-[9px] uppercase font-bold text-slate-400 tracking-wider">Qty</span>
                <span className="text-xs font-extrabold text-[#136bce] bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-lg mt-0.5">
                  {filteredFactories.length}
                </span>
              </div>

              <div className="w-full h-[1px] bg-slate-100" />

              {/* Compact List of Circles */}
              <div className="flex flex-col gap-3 w-full items-center max-h-[500px] overflow-y-auto pt-1 scrollbar-none">
                {filteredFactories.map((factory) => {
                  const isSelected = factory.name === selectedFactoryName;
                  const initials = factory.name
                    ? factory.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
                    : 'F';
                  const subStatus = factory.subscriptionStatus ?? 'Active';

                  const statusDotColor = 
                    subStatus === 'Trial' ? 'bg-blue-500' :
                    subStatus === 'Active' ? 'bg-emerald-500' :
                    subStatus === 'Grace' ? 'bg-amber-500' :
                    'bg-rose-500';

                  return (
                    <button
                      key={factory.name}
                      onClick={() => setSelectedFactoryName(factory.name)}
                      className={`relative w-11 h-11 rounded-xl flex items-center justify-center font-bold text-xs transition-all duration-200 group/item border ${
                        isSelected
                          ? 'bg-[#136bce] text-white border-[#136bce] shadow-md shadow-blue-500/15 scale-105 ring-2 ring-blue-100/40'
                          : 'bg-white text-slate-600 hover:bg-blue-50 hover:text-[#136bce] border-slate-200 hover:border-blue-250'
                      }`}
                    >
                      {/* Active indicator bar */}
                      {isSelected && (
                        <div className="absolute left-0 w-1 h-5 bg-white rounded-r-full" />
                      )}

                      <span>{initials}</span>

                      {/* Tooltip on hover */}
                      <div className="absolute left-14 bg-slate-900 text-white rounded-xl p-3 text-left text-[11px] font-normal leading-normal shadow-xl border border-slate-800 opacity-0 pointer-events-none group-hover/item:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 flex flex-col gap-1 min-w-[155px]">
                        <div className="flex items-center justify-between gap-2 border-b border-slate-800 pb-1 mb-1 font-bold text-white">
                          <span className="truncate max-w-[110px]">{factory.name}</span>
                          <span className={`w-2 h-2 rounded-full ring-1 ring-white/25 ${statusDotColor}`} />
                        </div>
                        <div className="text-slate-400 text-[10px] flex items-center gap-1">
                          <span className="material-symbols-outlined text-[11px]">location_on</span>
                          {factory.region}
                        </div>
                        <div className="text-slate-400 text-[10px] flex items-center gap-1">
                          <span className="material-symbols-outlined text-[11px]">card_membership</span>
                          Status: <span className="font-extrabold text-blue-300">{subStatus}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="w-full h-[1px] bg-slate-100" />

              {/* Compact Add Factory */}
              <button
                onClick={() => setShowAddFactoryModal(true)}
                title="Add New Factory"
                className="w-10 h-10 rounded-xl text-slate-500 hover:text-white hover:bg-blue-600 hover:border-blue-600 bg-slate-50 border border-slate-200/80 hover:shadow-md hover:shadow-blue-500/10 flex items-center justify-center transition-all active:scale-95"
              >
                <span className="material-symbols-outlined text-[18px]">add</span>
              </button>
            </>
          )}
        </div>

        {/* RIGHT COLUMN: Selected Factory Details + Users Table */}
        <div className={`transition-all duration-300 ${isSidebarOpen ? 'xl:col-span-8' : 'xl:col-span-11'} flex flex-col gap-8 w-full`}>
        
        {/* FACTORY DETAILS ON TOP */}
        {selectedFactory && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col gap-6 relative overflow-hidden">
            {selectedFactory.isDisabled && (
              <div className="bg-rose-50 border-b border-rose-100 px-6 py-2.5 -mx-6 -mt-6 mb-2 flex items-center justify-between text-xs font-semibold text-rose-800">
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[16px] text-rose-500">warning</span>
                  This factory has been disabled. Real-time inspections are paused.
                </span>
                {selectedFactory.subscriptionStatus !== 'Expired' && (
                  <span className="text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200 px-2 py-0.5 rounded-md uppercase tracking-wider">Pause Active</span>
                )}
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-4 flex-wrap">
                {!isSidebarOpen && (
                  <button
                    onClick={() => setIsSidebarOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-50/80 hover:bg-blue-100 text-[#136bce] ring-1 ring-blue-200 rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95 mr-2 shrink-0"
                    title="Expand Directory"
                  >
                    <span className="material-symbols-outlined text-[16px]">keyboard_double_arrow_right</span>
                    Expand Directory
                  </button>
                )}
                <div className="w-14 h-14 rounded-2xl bg-[#136bce]/10 text-[#136bce] flex items-center justify-center border border-blue-100">
                  <span className="material-symbols-outlined text-[32px]">factory</span>
                </div>
                <div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h3 className="text-xl font-bold text-slate-900 leading-tight">{selectedFactory.name}</h3>
                    {selectedFactory.isDisabled ? (
                      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                        Disabled
                      </span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        selectedFactory.status === 'Optimal' ? 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-600/20' :
                        selectedFactory.status === 'Warning' ? 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-600/20' :
                        'bg-red-50 text-red-800 ring-1 ring-inset ring-red-600/20'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          selectedFactory.status === 'Optimal' ? 'bg-emerald-500' :
                          selectedFactory.status === 'Warning' ? 'bg-amber-500' : 'bg-red-500'
                        }`}></span>
                        {selectedFactory.status}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5 font-medium">
                    <span className="material-symbols-outlined text-[15px] text-slate-400">location_on</span>
                    {selectedFactory.region}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <span className="text-xs text-slate-400 font-bold block text-left sm:text-right uppercase tracking-wider mb-1">Subscription Status</span>
                  <span className={`text-sm font-bold border px-3.5 py-1.5 rounded-xl block text-center capitalize ${
                    (selectedFactory.subscriptionStatus ?? 'Active') === 'Trial' ? 'bg-blue-50/70 text-blue-700 border-blue-200' :
                    (selectedFactory.subscriptionStatus ?? 'Active') === 'Active' ? 'bg-emerald-50/70 text-emerald-700 border-emerald-200' :
                    (selectedFactory.subscriptionStatus ?? 'Active') === 'Grace' ? 'bg-amber-50/70 text-amber-700 border-amber-200' :
                    (selectedFactory.subscriptionStatus ?? 'Active') === 'Expired' ? 'bg-rose-50/70 text-rose-700 border-rose-200' :
                    'bg-slate-50 text-slate-705 border-slate-200'
                  }`}>
                    {selectedFactory.subscriptionStatus ?? 'Active'}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 font-bold block text-left sm:text-right uppercase tracking-wider mb-1">Registration Date</span>
                  <span className="text-sm font-bold text-slate-800 bg-slate-50 border border-slate-100 px-3.5 py-1.5 rounded-xl block text-center">
                    {selectedFactory.enrolledDate}
                  </span>
                </div>
              </div>
            </div>

            {/* Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Inspections Checked</span>
                <h5 className="text-lg font-black text-slate-900 mt-1">{selectedFactory.inspections}</h5>
                <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">↑ Enterprise Sync Active</p>
              </div>

              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Device Cloud storage</span>
                <h5 className="text-lg font-black text-[#136bce] mt-1">{selectedFactory.storageGB} GB</h5>
                <p className="text-[10px] text-slate-500 font-semibold mt-0.5">PDFs & Media files</p>
              </div>

              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Active Users</span>
                <h5 className="text-lg font-black text-[#136bce] mt-1">
                  {users.filter(u => u.factoryName === selectedFactory.name && u.status === 'Active').length} / {selectedFactory.allowedUsers ?? 100}
                </h5>
                <p className="text-[10px] text-slate-500 font-semibold mt-0.5">Active / Allowed capacity</p>
              </div>

              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Outdated Devices</span>
                <h5 className={`text-lg font-black mt-1 ${selectedFactory.outdatedDevices > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                  {selectedFactory.outdatedDevices}
                </h5>
                <p className="text-[10px] text-slate-500 font-semibold mt-0.5">Devices requiring update</p>
              </div>
            </div>

            {/* Factory Contact & Profile Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl bg-slate-50/70 border border-slate-150 text-left">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1">
                  <span className="material-symbols-outlined text-[14px] text-slate-400">map</span>
                  Physical Address
                </span>
                <p className="text-xs font-semibold text-slate-800 leading-relaxed">
                  {selectedFactory.address || "No address configured"}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">Contact Person</span>
                  <p className="text-xs font-bold text-slate-800 mt-0.5 truncate" title={selectedFactory.contactPerson || ""}>
                    {selectedFactory.contactPerson || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">Designation</span>
                  <p className="text-xs font-bold text-slate-800 mt-0.5 truncate" title={selectedFactory.designation || ""}>
                    {selectedFactory.designation || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">Phone Number</span>
                  <p className="text-xs font-bold text-slate-800 mt-0.5 text-slate-800 truncate" title={selectedFactory.phoneNumber || ""}>
                    {selectedFactory.phoneNumber || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Factory URL and Allowed Users Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-150">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex-wrap flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">link</span>
                    Factory URL
                  </span>
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-2 pl-3">
                    <span className="text-xs text-slate-600 font-mono select-all truncate bg-transparent flex-1">
                      {`https://inspect.qamate.com/node/${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    </span>
                    <button
                      onClick={() => {
                        const url = `https://inspect.qamate.com/node/${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                        navigator.clipboard.writeText(url);
                        setCopiedFactoryName(selectedFactory.name);
                        setTimeout(() => setCopiedFactoryName(null), 2000);
                      }}
                      className="flex items-center justify-center p-1.5 text-slate-500 hover:text-[#136bce] hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all active:scale-95 bg-white shadow-sm"
                      title="Copy URL"
                    >
                      <span className="material-symbols-outlined text-[16px]">
                        {copiedFactoryName === selectedFactory.name ? 'check' : 'content_copy'}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">api</span>
                    API URL
                  </span>
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-2 pl-3">
                    <span className="text-xs text-slate-600 font-mono select-all truncate bg-transparent flex-1">
                      {`https://api.qamate.com/v1/node/${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    </span>
                    <button
                      onClick={() => {
                        const url = `https://api.qamate.com/v1/node/${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                        navigator.clipboard.writeText(url);
                        setCopiedFactoryName("api_url");
                        setTimeout(() => setCopiedFactoryName(null), 2000);
                      }}
                      className="flex items-center justify-center p-1.5 text-slate-500 hover:text-[#136bce] hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all active:scale-95 bg-white shadow-sm"
                      title="Copy API URL"
                    >
                      <span className="material-symbols-outlined text-[14px]">
                        {copiedFactoryName === "api_url" ? 'check' : 'content_copy'}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">shop_two</span>
                    Play Store URL
                  </span>
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-2 pl-3">
                    <span className="text-xs text-slate-600 font-mono select-all truncate bg-transparent flex-1">
                      https://play.google.com/store/apps/details?id=com.qamate.inspect
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText("https://play.google.com/store/apps/details?id=com.qamate.inspect");
                        setCopiedFactoryName("playstore");
                        setTimeout(() => setCopiedFactoryName(null), 2000);
                      }}
                      className="flex items-center justify-center p-1.5 text-slate-500 hover:text-[#136bce] hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all active:scale-95 bg-white shadow-sm"
                      title="Copy Play Store URL"
                    >
                      <span className="material-symbols-outlined text-[16px]">
                        {copiedFactoryName === "playstore" ? 'check' : 'content_copy'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-1.5 justify-between h-full">
                <div className="bg-emerald-50/10 border border-emerald-500/10 p-4 rounded-xl flex flex-col justify-between gap-3 h-full">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-emerald-800 tracking-wider flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[16px] text-emerald-600">share</span>
                      WhatsApp Dispatcher
                    </span>
                    <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                      Share active factory configuration including node credentials, URLs, and play store installation guides directly with local managers or tablet setup specialists.
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2 mt-1">
                    <button
                      onClick={() => {
                        const fUrl = `https://inspect.qamate.com/node/${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                        const pUrl = "https://play.google.com/store/apps/details?id=com.qamate.inspect";
                        
                        const adminUser = users.find(u => u.factoryName === selectedFactory.name && u.role === "Admin");
                        const adminEmail = adminUser?.email || `admin.${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '')}@qamate.com`;
                        const adminPassword = (adminUser as any)?.password || `Admin@${selectedFactory.name.replace(/[^a-zA-Z0-9]/g, '')}2026`;
                        
                        const msgText = `🏢 *QA-Mate Inspect Pro - Factory Node Board*\n\n` +
                          `• *Factory Name:* ${selectedFactory.name}\n` +
                          `• *Play Store Link:* ${pUrl}\n` +
                          `• *Factory URL:* ${fUrl}\n` +
                          `• *Admin Email:* ${adminEmail}\n` +
                          `• *Admin Password:* ${adminPassword}`;

                        navigator.clipboard.writeText(msgText);
                        setCopiedFactoryName("whatsapp_copy");
                        setTimeout(() => setCopiedFactoryName(null), 2000);
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-slate-100 hover:bg-slate-250 text-slate-700 font-bold rounded-xl text-xs transition-all shadow-sm active:scale-95 border border-slate-200"
                    >
                      <span className="material-symbols-outlined text-[15px]">content_copy</span>
                      {copiedFactoryName === "whatsapp_copy" ? "Copied!" : "Copy Details"}
                    </button>

                    <button
                      onClick={() => {
                        const fUrl = `https://inspect.qamate.com/node/${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                        const pUrl = "https://play.google.com/store/apps/details?id=com.qamate.inspect";
                        
                        const adminUser = users.find(u => u.factoryName === selectedFactory.name && u.role === "Admin");
                        const adminEmail = adminUser?.email || `admin.${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '')}@qamate.com`;
                        const adminPassword = (adminUser as any)?.password || `Admin@${selectedFactory.name.replace(/[^a-zA-Z0-9]/g, '')}2026`;
                        
                        const msgText = `🏢 *QA-Mate Inspect Pro - Factory Node Board*\n\n` +
                          `• *Factory Name:* ${selectedFactory.name}\n` +
                          `• *Play Store Link:* ${pUrl}\n` +
                          `• *Factory URL:* ${fUrl}\n` +
                          `• *Admin Email:* ${adminEmail}\n` +
                          `• *Admin Password:* ${adminPassword}`;

                        navigator.clipboard.writeText(msgText);
                        const wUrl = `https://wa.me/?text=${encodeURIComponent(msgText)}`;
                        window.open(wUrl, '_blank');
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition-all shadow-sm active:scale-95"
                    >
                      <span className="material-symbols-outlined text-[15px]">send</span>
                      Send via Whatsapp
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription details & Option to disable Factory */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-150">
              <div className="flex flex-col gap-2 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[16px] text-[#136bce]">workspace_premium</span>
                  Subscription plan & contract
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1 text-[11px]">
                  <div>
                    <span className="block text-[9px] text-slate-400 uppercase font-bold mb-1">Status</span>
                    <select
                      value={selectedFactory.subscriptionStatus ?? 'Active'}
                      onChange={(e) => {
                        const val = e.target.value as 'Trial' | 'Active' | 'Grace' | 'Expired';
                        setFactories(factories.map(f => {
                          if (f.name === selectedFactory.name) {
                            return { ...f, subscriptionStatus: val };
                          }
                          return f;
                        }));
                      }}
                      className="bg-white border border-slate-200 rounded-lg h-9 px-2.5 text-xs font-bold text-slate-800 w-full focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] shadow-xs cursor-pointer"
                    >
                      <option value="Trial">Trial</option>
                      <option value="Active">Active</option>
                      <option value="Grace">Grace</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>
                  <div>
                    <span className="block text-[9px] text-slate-400 uppercase font-bold mb-1">Start Date</span>
                    <input
                      type="date"
                      value={dateToYMD(selectedFactory.subscriptionStart ?? selectedFactory.enrolledDate)}
                      onChange={(e) => {
                        const val = ymdToReadable(e.target.value);
                        setFactories(factories.map(f => {
                          if (f.name === selectedFactory.name) {
                            return { ...f, subscriptionStart: val };
                          }
                          return f;
                        }));
                      }}
                      className="bg-white border border-slate-200 rounded-lg h-9 px-2.5 text-xs font-bold text-slate-800 w-full focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] shadow-xs"
                    />
                  </div>
                  <div>
                    <span className="block text-[9px] text-[#136bce] sm:text-slate-400 uppercase font-bold mb-1">Expiry Date</span>
                    <input
                      type="date"
                      value={dateToYMD(selectedFactory.subscriptionEnd ?? '15 Jan 2027')}
                      onChange={(e) => {
                        const val = ymdToReadable(e.target.value);
                        setFactories(factories.map(f => {
                          if (f.name === selectedFactory.name) {
                            return { ...f, subscriptionEnd: val };
                          }
                          return f;
                        }));
                      }}
                      className="bg-white border border-slate-200 rounded-lg h-9 px-2.5 text-xs font-bold text-slate-800 w-full focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] shadow-xs"
                    />
                  </div>
                  <div>
                    <span className="block text-[9px] text-slate-400 uppercase font-bold mb-1">Allowed Seats</span>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={selectedFactory.allowedUsers ?? 100}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10) || 100;
                        setFactories(factories.map(f => {
                          if (f.name === selectedFactory.name) {
                            return { ...f, allowedUsers: val };
                          }
                          return f;
                        }));
                      }}
                      className="bg-white border border-slate-200 rounded-lg h-9 px-2.5 text-xs font-bold text-slate-800 w-full focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] shadow-xs"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-between bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <div className="flex items-center justify-between gap-4 h-full">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[16px] text-slate-500">do_not_disturb_on</span>
                      Disable / Deactivate Factory
                    </span>
                    <p className="text-[10px] text-slate-500 mt-1 font-medium leading-relaxed">
                      {selectedFactory.isDisabled
                        ? "Deactivated: Tablet devices are locked, active inspection runs are suspended, and sync permissions are revoked."
                        : "Operational: Allowed to perform real-time checklists. Disabling will halt active inspector sessions instantly."
                      }
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmFactoryState(selectedFactory.name)}
                    className={`shrink-0 px-4 py-2 rounded-xl text-xs font-black transition-all active:scale-95 border shadow-sm ${
                      selectedFactory.isDisabled
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                        : 'bg-rose-50 border-rose-150 text-rose-600 hover:bg-rose-100'
                    }`}
                  >
                    {selectedFactory.isDisabled ? 'Enable Factory' : 'Disable Factory'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* USERS LIST BOARD */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col gap-6 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Enrolled QA Staff ({filteredUsers.length})</h3>
              <p className="text-xs text-slate-500 mt-0.5">Tablet devices operating at this factory node</p>
            </div>

            <div className="flex items-center gap-3 self-end sm:self-center">
              <button
                onClick={() => setShowAddUserModal(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 bg-[#136bce] hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all whitespace-nowrap"
              >
                <span className="material-symbols-outlined text-[18px]">person_add</span>
                Add New User
              </button>
            </div>
          </div>

          {/* Tabs for Admin and Staff role selection */}
          <div className="flex border-b border-slate-200 -mx-6 px-6 -mt-2">
            <button
              type="button"
              onClick={() => setRoleTab('Staff')}
              className={`flex items-center gap-2 px-5 py-3 text-xs font-bold border-b-2 transition-all ${
                roleTab === 'Staff'
                  ? 'border-[#136bce] text-[#136bce] bg-blue-50/10'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">badge</span>
              Staff / Inspectors ({selectedFactoryUsers.filter(u => (u.role || 'Staff') === 'Staff').length})
            </button>
            <button
              type="button"
              onClick={() => setRoleTab('Admin')}
              className={`flex items-center gap-2 px-5 py-3 text-xs font-bold border-b-2 transition-all ${
                roleTab === 'Admin'
                  ? 'border-[#136bce] text-[#136bce] bg-blue-50/10'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">admin_panel_settings</span>
              Admin / Leads ({selectedFactoryUsers.filter(u => u.role === 'Admin').length})
            </button>
          </div>

          {/* User Search & Version Filter Panel */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
            <div className="relative flex-1">
              <input
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-4 text-xs placeholder-slate-400 focus:border-[#136bce] focus:outline-none focus:ring-1 focus:ring-[#136bce] bg-white"
                placeholder="Search QA team members by name, email or EMP ID..."
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
              <span className="material-symbols-outlined absolute left-3 top-2.5 text-[18px] text-slate-400">search</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">App Version:</span>
              <select
                className="rounded-lg border border-slate-200 bg-white py-1.5 pl-3 pr-8 text-xs focus:border-[#136bce] focus:outline-none text-slate-700 font-semibold cursor-pointer shadow-xs"
                value={userVersionFilter}
                onChange={(e) => setUserVersionFilter(e.target.value)}
              >
                <option value="All">All Versions</option>
                {userVersions.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* User List Table */}
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm text-slate-600 min-w-[700px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase font-bold tracking-wider text-slate-500">
                <tr>
                   <th className="px-6 py-4">User Name & Contact</th>
                   <th className="px-6 py-4">Last Logged In</th>
                   <th className="px-6 py-4 text-center">No. of Inspections</th>
                   <th className="px-6 py-4">App Version</th>
                   <th className="px-6 py-4 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-slate-400">
                      <span className="material-symbols-outlined text-[36px] block text-slate-300 mb-2">supervisor_account</span>
                      No enrolled inspectors found matching current search/filter.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr 
                      key={user.id} 
                      onClick={() => setSelectedUserForDetail(user)}
                      className="hover:bg-blue-50/40 transition-colors cursor-pointer group"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={getUserAvatarUrl(user.name)}
                            alt={user.name}
                            referrerPolicy="no-referrer"
                            className="w-9 h-9 rounded-full object-cover border border-slate-200 shadow-sm animate-fade-in"
                          />
                          <div>
                            <p className="font-bold text-slate-900 leading-tight">{user.name}</p>
                            <p className="text-[11px] text-slate-500 mt-0.5">{user.email}</p>
                            <p className="text-[10px] text-slate-400 font-mono mt-1 flex items-center gap-1 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded w-max">
                              <span className="material-symbols-outlined text-[12px] text-slate-400">fingerprint</span>
                              Staff ID: <span className="font-bold text-slate-700">{user.employeeId}</span>
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-500 font-medium">
                        {user.lastLoggedIn}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-mono font-bold text-slate-900 bg-slate-100/70 px-2 py-1 rounded text-xs border border-slate-200/50">
                          {user.inspectionsCount}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                          user.appVersion === selectedFactory?.appVersion 
                            ? 'bg-blue-50 text-blue-700 border border-blue-100'
                            : 'bg-red-50 text-red-700 border border-red-100 animate-pulse-subtle'
                        }`}>
                          {user.appVersion}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                          user.status === 'Active' 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                            : 'bg-slate-100 text-slate-500 border border-slate-200'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${user.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                          {user.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

      {/* ADDFONT DIALOGS / MODALS: ADD FACTORY MODAL */}
      <AnimatePresence>
        {showAddFactoryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-6 shadow-xl flex flex-col gap-5 overflow-y-auto max-h-[90dvh]"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#136bce]">factory</span>
                  <h3 className="text-lg font-bold text-slate-900">Add New Factory</h3>
                </div>
                <button
                  onClick={() => setShowAddFactoryModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleCreateFactory} className="flex flex-col gap-5">
                {/* SECTION 1: FACILITY PROFILE */}
                <div className="flex flex-col gap-3">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">info</span>
                    Facility Profile
                  </span>
                  
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Factory Name *</label>
                    <input
                      required
                      className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                      placeholder="e.g. Apex Apparel Tech (Facility C)"
                      type="text"
                      value={newFactory.name}
                      onChange={(e) => setNewFactory({ ...newFactory, name: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Location / District *</label>
                      <input
                        required
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. Vietnam (HCMC)"
                        type="text"
                        value={newFactory.region}
                        onChange={(e) => setNewFactory({ ...newFactory, region: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Street Address *</label>
                      <input
                        required
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. Plot No. 12, Ind Area, NY"
                        type="text"
                        value={newFactory.address}
                        onChange={(e) => setNewFactory({ ...newFactory, address: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* SECTION 2: REPRESENTATIVE CONTACT INFO */}
                <div className="flex flex-col gap-3">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">person</span>
                    Contact Representative
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Contact Person (Optional)</label>
                      <input
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. Mr. Shanmugam"
                        type="text"
                        value={newFactory.contactPerson}
                        onChange={(e) => setNewFactory({ ...newFactory, contactPerson: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Phone Number (Optional)</label>
                      <input
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-900 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. +91 94450 12345"
                        type="text"
                        value={newFactory.phoneNumber}
                        onChange={(e) => setNewFactory({ ...newFactory, phoneNumber: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Designation / Role (Optional)</label>
                    <input
                      className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-900 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                      placeholder="e.g. Plant Manager / Lead Inspector"
                      type="text"
                      value={newFactory.designation}
                      onChange={(e) => setNewFactory({ ...newFactory, designation: e.target.value })}
                    />
                  </div>
                </div>

                {/* SUBSCRIPTION PLAN & CONTRACT */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col gap-3 text-left">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-200/50 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">workspace_premium</span>
                    Subscription Plan & Contract
                  </span>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Status</label>
                      <select
                        value={newFactory.subscriptionStatus}
                        onChange={(e) => setNewFactory({ ...newFactory, subscriptionStatus: e.target.value as any })}
                        className="w-full h-9 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-800 focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] cursor-pointer"
                      >
                        <option value="Trial">Trial</option>
                        <option value="Active">Active</option>
                        <option value="Grace">Grace</option>
                        <option value="Expired">Expired</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Seats</label>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        value={newFactory.allowedUsers}
                        onChange={(e) => setNewFactory({ ...newFactory, allowedUsers: parseInt(e.target.value, 10) || 1 }) }
                        className="w-full h-9 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-800 focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce]"
                      />
                    </div>

                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Start Date</label>
                      <input
                        type="date"
                        value={newFactory.subscriptionStart}
                        onChange={(e) => setNewFactory({ ...newFactory, subscriptionStart: e.target.value })}
                        className="w-full h-9 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-800 focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce]"
                      />
                    </div>

                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Expiry Date</label>
                      <input
                        type="date"
                        value={newFactory.subscriptionEnd}
                        onChange={(e) => setNewFactory({ ...newFactory, subscriptionEnd: e.target.value })}
                        className="w-full h-9 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-800 focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce]"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowAddFactoryModal(false)}
                    className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl text-xs font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-[#136bce] text-white hover:bg-blue-700 rounded-xl text-xs font-bold transition-all shadow-sm shadow-blue-500/20"
                  >
                    Register Factory
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* EDIT FACTORY MODAL */}
      <AnimatePresence>
        {showEditFactoryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm shadow-2xl">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-6 shadow-xl flex flex-col gap-5 overflow-y-auto max-h-[90dvh]"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#136bce]">edit_note</span>
                  <h3 className="text-lg font-bold text-slate-900">Edit Factory Profile</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowEditFactoryModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleEditFactory} className="flex flex-col gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono">Factory Name *</label>
                  <input
                    required
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-sans"
                    placeholder="e.g. Apex Apparel Tech (Facility C)"
                    type="text"
                    value={editFactoryForm.name}
                    onChange={(e) => setEditFactoryForm({ ...editFactoryForm, name: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono">Location / District *</label>
                    <input
                      required
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-sans"
                      placeholder="e.g. Vietnam (HCMC)"
                      type="text"
                      value={editFactoryForm.region}
                      onChange={(e) => setEditFactoryForm({ ...editFactoryForm, region: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono font-medium text-slate-500">Contact Person (Optional)</label>
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-955 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-sans"
                      placeholder="e.g. Mr. Shanmugam"
                      type="text"
                      value={editFactoryForm.contactPerson}
                      onChange={(e) => setEditFactoryForm({ ...editFactoryForm, contactPerson: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono">Address *</label>
                  <textarea
                    required
                    rows={2}
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none resize-none font-sans"
                    placeholder="e.g. Plot No. 12, Ind Area, NH-7 Bypass"
                    value={editFactoryForm.address}
                    onChange={(e) => setEditFactoryForm({ ...editFactoryForm, address: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono font-medium text-slate-500">Designation (Optional)</label>
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-900 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-sans"
                      placeholder="e.g. Plant Manager"
                      type="text"
                      value={editFactoryForm.designation}
                      onChange={(e) => setEditFactoryForm({ ...editFactoryForm, designation: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono font-medium text-slate-500">Phone Number (Optional)</label>
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-900 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-sans"
                      placeholder="e.g. +91 94450 12345"
                      type="text"
                      value={editFactoryForm.phoneNumber}
                      onChange={(e) => setEditFactoryForm({ ...editFactoryForm, phoneNumber: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-4 border-t border-slate-100 font-sans">
                  <button
                    type="button"
                    onClick={() => setShowEditFactoryModal(false)}
                    className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl text-xs font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-[#136bce] text-white hover:bg-blue-700 rounded-xl text-xs font-bold transition-all shadow-sm shadow-blue-500/20"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* DISABLE / ENABLE FACTORY CONFIRMATION MODAL */}
      <AnimatePresence>
        {confirmFactoryState && (() => {
          const factoryObj = factories.find(f => f.name === confirmFactoryState);
          if (!factoryObj) return null;
          const isCurrentlyDisabled = factoryObj.isDisabled;

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl border border-slate-200 w-full max-w-md p-6 shadow-xl flex flex-col gap-5 text-left"
              >
                <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isCurrentlyDisabled ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                  }`}>
                    <span className="material-symbols-outlined text-[20px]">
                      {isCurrentlyDisabled ? 'power' : 'dangerous'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      {isCurrentlyDisabled ? 'Enable Factory Node?' : 'Disable Factory Node?'}
                    </h3>
                    <p className="text-[11px] text-slate-500">Security Clearance Required</p>
                  </div>
                </div>

                <div className="text-xs text-slate-600 leading-relaxed space-y-2 font-sans">
                  <p>
                    You are changing the operational authorization for <span className="font-bold text-slate-800">{factoryObj.name}</span> located in <span className="font-bold text-slate-800">{factoryObj.region}</span>.
                  </p>
                  {isCurrentlyDisabled ? (
                    <p className="bg-emerald-50/50 border border-emerald-100 text-emerald-800 p-3 rounded-xl">
                      <strong>Note:</strong> Enabling this factory will immediately restore inspection access for all enrolled tablet devices. Inspectors can start syncing batch logs.
                    </p>
                  ) : (
                    <p className="bg-rose-50/50 border border-rose-150 text-rose-800 p-3 rounded-xl font-medium">
                      <strong>Warning:</strong> Disabling this factory will suspend all active inspections. Terminals will NOT be permitted to start new checklists, upload media, or sync audits until re-enabled.
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setConfirmFactoryState(null)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Toggle isDisabled status
                      setFactories(factories.map(f => {
                        if (f.name === factoryObj.name) {
                          return { ...f, isDisabled: !f.isDisabled };
                        }
                        return f;
                      }));
                      setConfirmFactoryState(null);
                    }}
                    className={`px-4 py-2 text-white rounded-xl text-xs font-bold transition-all shadow-sm ${
                      isCurrentlyDisabled
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-rose-600 hover:bg-rose-700'
                    }`}
                  >
                    {isCurrentlyDisabled ? 'Confirm Enable' : 'Confirm Disable'}
                  </button>
                </div>
              </motion.div>
            </div>
          );
        })()}
      </AnimatePresence>

      {/* ADD USER MODAL */}
      <AnimatePresence>
        {showAddUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-6 shadow-xl flex flex-col gap-5"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#136bce]">person_add</span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Add New User</h3>
                    <p className="text-[11px] text-slate-500">Create a new user account for: {selectedFactoryName}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddUserModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="flex flex-col gap-5">
                {/* SECTION 1: USER PROFILE */}
                <div className="flex flex-col gap-3">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">badge</span>
                    User Profile
                  </span>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Full Name *</label>
                    <input
                      required
                      className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                      placeholder="e.g. Sarah Jenkins"
                      type="text"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Email ID *</label>
                      <input
                        required
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. sarah.jenkins@qamate.com"
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Employee ID *</label>
                      <input
                        required
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. EMP-2023-045"
                        type="text"
                        value={newUser.employeeId}
                        onChange={(e) => setNewUser({ ...newUser, employeeId: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* SECTION 2: REPRESENTATIVE CONFIG */}
                <div className="flex flex-col gap-3">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">admin_panel_settings</span>
                    Role & Status
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">USER ROLE *</label>
                      <div className="grid grid-cols-2 gap-2 font-semibold">
                        <button
                          type="button"
                          onClick={() => setNewUser({ ...newUser, role: 'Staff' })}
                          className={`flex items-center justify-center gap-1.5 h-10 rounded-xl border text-xs font-bold transition-all ${
                            newUser.role === 'Staff'
                              ? 'border-[#136bce] bg-blue-50/20 text-[#136bce] ring-1 ring-[#136bce]'
                              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <span className="material-symbols-outlined text-[14px]">badge</span>
                          Staff
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewUser({ ...newUser, role: 'Admin' })}
                          className={`flex items-center justify-center gap-1.5 h-10 rounded-xl border text-xs font-bold transition-all ${
                            newUser.role === 'Admin'
                              ? 'border-[#136bce] bg-blue-50/20 text-[#136bce] ring-1 ring-[#136bce]'
                              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <span className="material-symbols-outlined text-[14px]">admin_panel_settings</span>
                          Admin
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Employee Status</label>
                      <select
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs text-slate-800 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none cursor-pointer"
                        value={newUser.status}
                        onChange={(e) => setNewUser({ ...newUser, status: e.target.value as any })}
                      >
                        <option value="Active">Active</option>
                        <option value="Disabled">Disabled</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* SECTION 3: PERMISSIONS */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col gap-3 text-left">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-200/50 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">category</span>
                    Select Modules *
                  </span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                    {['Pilot', 'Fabric', 'Line', 'Final', 'Container'].map((mod) => {
                      const isSelected = (newUser.modules || []).includes(mod);
                      return (
                        <button
                          key={mod}
                          type="button"
                          onClick={() => {
                            const current = newUser.modules || [];
                            if (current.includes(mod)) {
                              setNewUser({ ...newUser, modules: current.filter(m => m !== mod) });
                            } else {
                              setNewUser({ ...newUser, modules: [...current, mod] });
                            }
                          }}
                          className={`flex items-center gap-1.5 h-10 px-3 rounded-xl border text-left text-xs font-semibold transition-all ${
                            isSelected
                              ? 'border-[#136bce] bg-blue-50/20 text-[#136bce] ring-1 ring-[#136bce]'
                              : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
                          }`}
                        >
                          <span className="material-symbols-outlined text-[16px]">
                            {isSelected ? 'check_box' : 'check_box_outline_blank'}
                          </span>
                          <span>{mod}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowAddUserModal(false)}
                    className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl text-xs font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-[#136bce] text-white hover:bg-blue-700 rounded-xl text-xs font-bold transition-all shadow-sm shadow-blue-500/20"
                  >
                    Add User
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
 
       {/* USER DETAIL DESCRIPTION MODAL */}
       <AnimatePresence>
         {selectedUserForDetail && (() => {
           const details = getDeviceDetails(selectedUserForDetail);
           return (
             <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
               <motion.div
                 initial={{ scale: 0.95, opacity: 0 }}
                 animate={{ scale: 1, opacity: 1 }}
                 exit={{ scale: 0.95, opacity: 0 }}
                 className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-6 shadow-xl flex flex-col gap-5 overflow-y-auto max-h-[90dvh]"
               >
                 <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                   <div className="flex items-center gap-3">
                     <img
                       src={getUserAvatarUrl(selectedUserForDetail.name)}
                       alt={selectedUserForDetail.name}
                       referrerPolicy="no-referrer"
                       className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-sm"
                     />
                     <div>
                       <h3 className="text-base font-bold text-slate-900">{selectedUserForDetail.name}</h3>
                       <p className="text-xs text-slate-500 font-mono flex items-center gap-1.5 mt-0.5">
                         <span className="material-symbols-outlined text-[12px]">fingerprint</span>
                         Employee ID: {selectedUserForDetail.employeeId}
                       </p>
                     </div>
                   </div>
                   <button
                     onClick={() => setSelectedUserForDetail(null)}
                     className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                   >
                     <span className="material-symbols-outlined">close</span>
                   </button>
                 </div>
 
                 <div className="space-y-4">
                   {/* Account Information */}
                   <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                     <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Operator Profiles</h4>
                     <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-xs">
                       <div>
                         <span className="text-slate-400 block mb-0.5">Primary Email</span>
                         <span className="font-semibold text-slate-800 break-all">{selectedUserForDetail.email}</span>
                       </div>
                       <div>
                         <span className="text-slate-400 block mb-0.5">Facility Assignment</span>
                         <span className="font-semibold text-slate-800">{selectedUserForDetail.factoryName}</span>
                       </div>
                       <div>
                         <span className="text-slate-400 block mb-0.5">Inspection Status</span>
                         <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                           {selectedUserForDetail.status}
                         </span>
                       </div>
                       <div>
                         <span className="text-slate-400 block mb-0.5">Last Connection</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block mb-0.5">Authority Role</span>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                            selectedUserForDetail.role === 'Admin' ? 'bg-purple-150 text-purple-800 border border-purple-200 bg-purple-50' : 'bg-blue-150 text-blue-800 border border-blue-200 bg-blue-50'
                          }`}>
                            {selectedUserForDetail.role || 'Staff'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block mb-0.5">Last Connection</span>
                         <span className="font-semibold text-slate-800">{selectedUserForDetail.lastLoggedIn}</span>
                       </div>
                     </div>
                   </div>
 
                   {/* Tablet Specs */}
                   {/* Active License Modules Enabled */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[15px] text-[#136bce]">fact_check</span>
                        Active QA Modules Enabled
                      </h4>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(selectedUserForDetail.modules || ['Pilot', 'Fabric', 'Line', 'Final', 'Container']).map((mod) => (
                          <span key={mod} className="bg-blue-50/60 text-[#136bce] px-2.5 py-1 rounded-xl text-[10px] font-bold border border-blue-100/40">
                            {mod}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-blue-50/30 p-4 rounded-xl border border-blue-100/40">
                     <h4 className="text-xs font-bold text-[#136bce] uppercase tracking-widest mb-3 flex items-center gap-1">
                       <span className="material-symbols-outlined text-[15px]">tablet_mac</span>
                       Tablet Device Hardware Specs
                     </h4>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3.5 gap-x-4 text-xs">
                       <div className="md:col-span-2">
                         <span className="text-slate-500 block mb-0.5">Device Name</span>
                         <span className="font-bold text-slate-800">{details.deviceName}</span>
                       </div>
                       <div>
                         <span className="text-slate-500 block mb-0.5">Operating System</span>
                         <span className="font-semibold text-slate-800">{details.osVersion}</span>
                       </div>
                       <div>
                         <span className="text-slate-500 block mb-0.5">QA App Version</span>
                         <span className="font-semibold text-slate-800 bg-white px-2 py-0.5 border border-slate-200 rounded text-[11px]">
                           {selectedUserForDetail.appVersion}
                         </span>
                       </div>
                       <div>
                         <span className="text-slate-500 block mb-0.5">RAM Capacity</span>
                         <span className="font-semibold text-slate-800">{details.ram}</span>
                       </div>
                       <div>
                         <span className="text-slate-500 block mb-0.5">Device Storage</span>
                         <span className="font-semibold text-slate-800">{details.storage}</span>
                       </div>
                       <div>
                         <span className="text-slate-500 block mb-0.5">Screen Dimensions</span>
                         <span className="font-semibold text-slate-800">{details.screenSize}</span>
                       </div>
                       <div>
                         <span className="text-slate-500 block mb-0.5">Cellular Connection</span>
                         <span className="font-semibold text-slate-800">{details.cellularType}</span>
                       </div>
                       <div className="md:col-span-2 border-t border-slate-200/50 pt-2.5 mt-1">
                         <div className="flex items-center justify-between">
                           <div>
                             <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Total Usage Time</span>
                             <p className="font-bold text-slate-900 mt-0.5">{details.usageTime}</p>
                           </div>
                           <div className="text-right">
                             <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Completed Inspections</span>
                             <p className="font-bold text-[#136bce] mt-0.5 text-sm">{selectedUserForDetail.inspectionsCount} batches</p>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
 
                 <div className="flex justify-end pt-2">
                   <button
                     onClick={() => setSelectedUserForDetail(null)}
                     className="px-5 py-2.5 bg-slate-950 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                   >
                     Close Hardware Telemetry
                   </button>
                 </div>
               </motion.div>
             </div>
           );
         })()}
       </AnimatePresence>
     </div>
  );
};
